import {
  CheckboxContainer,
  CheckboxTitleContainer,
} from "@/components/layout/CheckboxContainer";
import { type BokaQuestionnaireData } from "@/features/project/useProjectQuestionnaire";
import { type QuestionObject } from "@repo/api";
import { Checkbox, Option, RadioButtonGroup, Select, Text } from "@repo/skapa";
import { T, t, useTranslation } from "@repo/translation";
import {
  SectionContainer,
  localizeDateTimeRange,
  translateServices,
  useSettings,
} from "@repo/ui";
import { hasItems, trackAnalytics } from "@repo/utils";
import isEmpty from "lodash/isEmpty";
import { type ChangeEvent } from "react";

interface QuestionItemProps {
  question: QuestionObject;
}

const getText = (
  text: Record<string, string>,
  language: string,
  defaultLang?: string
) => {
  const questionText =
    text[language] ??
    (defaultLang ? text[defaultLang] : Object.values(text)[0]);

  if (questionText !== undefined) {
    return questionText;
  }

  // If we dind't find any language we expected, try to use whatever is there
  const fallbackLangKey = Object.keys(text ?? {})[0];

  return text[fallbackLangKey];
};

const QuestionItem = ({ question }: QuestionItemProps) => {
  const { language, general } = useSettings();

  if (isEmpty(question.question) || isEmpty(question.question.text)) {
    return <></>;
  }

  const defaultLang = general?.default_language_code;
  const questionText = getText(question.question.text, language, defaultLang);

  if (question.question.type === "TEXT") {
    return (
      <div>
        <Text textStyle="Heading.XS" wordBreakWord>
          {questionText}
        </Text>
        <Text textStyle="Body.S" wordBreakWord>
          {question.answer.answer}
        </Text>
      </div>
    );
  }

  if (question.question.type === "RADIO") {
    return (
      <div>
        <RadioButtonGroup
          name={questionText}
          list={question.question.options.map((option) => ({
            name: option.id.toString(),
            id: option.id.toString(),
            label: getText(option.text, language, defaultLang),
            value: option.id.toString(),
            disabled: !question.answer.answer.includes(String(option.id)),
            defaultChecked: question.answer.answer.includes(String(option.id)),
          }))}
        />
      </div>
    );
  }

  if (question.question.type === "CHECKBOX") {
    return (
      <div>
        <CheckboxTitleContainer>
          <Text textStyle="Heading.XS" wordBreakWord>
            {questionText}
          </Text>
        </CheckboxTitleContainer>
        {question.question.options.map((option, index) => (
          <CheckboxContainer key={option.id ?? `index-${index}`}>
            <Checkbox
              id={String(option.id)}
              label={getText(option.text, language, defaultLang)}
              defaultChecked={question.answer.answer.includes(
                String(option.id)
              )}
              disabled={!question.answer.answer.includes(String(option.id))}
              value={String(option.id)}
            />
          </CheckboxContainer>
        ))}
      </div>
    );
  }

  return <></>;
};

export const AppointmentQuestionnaire = ({
  questionnaires,
  selectedQuestionnaire,
  onSelectionChange,
  questions,
}: BokaQuestionnaireData) => {
  const { dateFnsLocale } = useTranslation();

  if (!selectedQuestionnaire) {
    return (
      <Text>
        <T translationKey="prompt.no_questionnaire" />
      </Text>
    );
  }

  return (
    <SectionContainer>
      <Select
        id="questionnaire-select"
        label={t("label.appointments")}
        value={String(selectedQuestionnaire.id)}
        onChange={(e: ChangeEvent<HTMLSelectElement>) => {
          onSelectionChange(Number(e.currentTarget.value));
          trackAnalytics("select_questionnaire");
        }}
      >
        {questionnaires.map((questionnaire) => (
          <Option
            key={questionnaire.id}
            value={String(questionnaire.id)}
            name={`${localizeDateTimeRange(
              questionnaire.startDate,
              questionnaire.endDate,
              dateFnsLocale
            )} | ${translateServices(questionnaire.service)} (${questionnaire.status})`}
          />
        ))}
      </Select>
      {hasItems(questions) ? (
        questions.map((question, index) => (
          <SectionContainer
            key={`${selectedQuestionnaire.id}-${question.question.id ?? `index-${index}`}`}
          >
            <QuestionItem question={question} />
          </SectionContainer>
        ))
      ) : (
        <Text>
          <T translationKey="prompt.no_questionnaire" />
        </Text>
      )}
    </SectionContainer>
  );
};
